import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { QueryCache, QueryClient } from "@tanstack/react-query";

import { clearClientCache } from "./cacheReset";
import { ApiError } from "./http";

/**
 * The app's single QueryClient. Query's cache is what makes navigating between
 * pages feel instant: cached data is shown immediately on remount while a
 * background refetch revalidates it (stale-while-revalidate). This replaces the
 * hand-rolled module caches we used to keep in board.ts / settings.ts.
 */

// If any query 401s, the session died server-side: reflect it and bounce to the
// login screen. The router is imported lazily to avoid a static import cycle
// (router.tsx imports this module).
async function handleUnauthorized() {
  // The session died server-side. Wipe the previous user's cache — including the
  // on-disk snapshot — so it can't be rehydrated for whoever logs in next on
  // this browser, then reflect the logout and bounce to /login.
  clearClientCache(queryClient, persister);
  queryClient.setQueryData(["session"], null);
  const { router } = await import("../router");
  void router.navigate({ to: "/login" });
}

const queryCache = new QueryCache({
  onError: (error, query) => {
    // The session probe itself returns null when logged out (never errors), so
    // any 401 here is a *previously authenticated* query — the session expired.
    if (
      error instanceof ApiError &&
      error.status === 401 &&
      query.queryKey[0] !== "session"
    ) {
      void handleUnauthorized();
    }
  },
});

export const queryClient = new QueryClient({
  queryCache,
  defaultOptions: {
    queries: {
      // Don't retry auth/not-found failures — they won't get better.
      retry: (failureCount, error) => {
        if (
          error instanceof ApiError &&
          (error.status === 401 || error.status === 404)
        ) {
          return false;
        }
        return failureCount < 1;
      },
      // Default off: most queries refetch on explicit invalidation (after
      // mutations), not on tab focus. Views that reflect out-of-band server
      // state (board, task detail, archived) opt back IN per-query, so a
      // scheduled/other-session sync shows on refocus without a reload.
      refetchOnWindowFocus: false,
      // Default: revalidate on mount, but keep showing cached data meanwhile.
      staleTime: 30_000,
    },
  },
});

/**
 * Persists the cache to localStorage so a hard reload paints instantly, then
 * revalidates. Exported so logout can wipe it (queryClient.clear() only empties
 * memory, not the on-disk snapshot — without this a second user on the same
 * machine could briefly see the previous user's data).
 */
export const persister = createSyncStoragePersister({
  storage: window.localStorage,
});

